import {
  AccessTime as AccessTimeIcon,
  ArrowForward as ArrowForwardIcon,
  Assessment as AssessmentIcon,
  Assignment as AssignmentIcon,
  CalendarToday as CalendarIcon,
  Description as DescriptionIcon,
  Event as EventIcon,
  Group as GroupIcon,
  MenuBook as MenuBookIcon,
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AddClientModal from '../components/AddClientModal';
import AppointmentModal from '../components/AppointmentModal';
import ClientDetailModal from '../components/ClientDetailModal';
import DietProgramModal from '../components/DietProgramModal';
import Loading from '../components/Loading';
import { useDietitianData } from '../hooks/useDietitianData';
import { appointmentApi } from '../services/appointmentApi';
import { Client, clientApi } from '../services/clientApi';
import {
  CreateDietProgramRequest,
  dietProgramApi,
} from '../services/dietProgramApi';
import { Meal, mealApi } from '../services/mealApi';
import { colors } from '../theme';

const DietitianDashboard: React.FC = () => {
  const navigate = useNavigate();
  const {
    dietitianId,
    clients,
    appointments,
    dietPrograms,
    loading,
    error,
    setClients,
    setAppointments,
    setDietPrograms,
    refreshClients,
    showSuccess,
    showError,
    showWarning,
  } = useDietitianData({
    fetchClients: true,
    fetchAppointments: true,
    fetchDietPrograms: true,
  });

  const [isAddClientModalOpen, setIsAddClientModalOpen] = useState(false);
  const [isAppointmentModalOpen, setIsAppointmentModalOpen] = useState(false);
  const [isDietProgramModalOpen, setIsDietProgramModalOpen] = useState(false);
  const [isClientDetailModalOpen, setIsClientDetailModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedClientTab, setSelectedClientTab] = useState<
    'info' | 'nutrition' | 'weight' | 'appointments' | 'programs'
  >('info');
  const [meals, setMeals] = useState<Meal[]>([]);
  const generateTempPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let password = '';
    for (let i = 0; i < 8; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  const handleAddClient = async (
    clientData: Omit<Client, 'id' | 'createdAt' | 'updatedAt'>
  ) => {
    try {
      const tempPassword = generateTempPassword();
      await clientApi.createClient(clientData).then(async (res) => {
        setClients((prev) => [...prev, res.client]);

        try {
          const { createUserWithEmailAndPassword } = await import(
            'firebase/auth'
          );
          const { auth } = await import('../firebase');

          await createUserWithEmailAndPassword(
            auth,
            clientData.email,
            tempPassword
          );

          const setPasswordUrl = `${window.location.origin
            }/client/set-password?email=${encodeURIComponent(
              clientData.email
            )}&temp=${tempPassword}`;

          try {
            const { emailApi } = await import('../services/emailApi');
            const result = await emailApi.sendInvitationEmail(
              clientData.email,
              `${clientData.firstName} ${clientData.lastName}`,
              tempPassword,
              setPasswordUrl
            );

            if (result.warning) {
              showWarning(
                `${clientData.firstName} ${clientData.lastName} başarıyla eklendi ancak davet email'i gönderilemedi.`,
                'Kısmi Başarı'
              );
            } else {
              showSuccess(
                `${clientData.firstName} ${clientData.lastName} başarıyla eklendi!`,
                'Danışan Eklendi'
              );
            }
          } catch (emailError) {
            showWarning(
              `${clientData.firstName} ${clientData.lastName} başarıyla eklendi ancak email gönderilemedi.`,
              'Kısmi Başarı'
            );
          }
        } catch (authError: any) {
          showWarning(
            `Danışan eklendi ancak giriş bilgileri oluşturulamadı.`,
            'Kısmi Başarı'
          );
        }
      });
      setIsAddClientModalOpen(false);
    } catch (err) {
      showError(
        'Danışan eklenirken bir sorun oluştu. Lütfen tekrar deneyin.',
        'Ekleme Hatası'
      );
    }
  };

  const handleAddAppointment = async (appointmentData: any) => {
    try {
      const response = await appointmentApi.createAppointment(appointmentData);
      setAppointments((prev) => [...prev, response.appointment]);
      showSuccess('Randevu başarıyla oluşturuldu!', 'Randevu Eklendi');
      setIsAppointmentModalOpen(false);
    } catch (err) {
      showError(
        'Randevu eklenirken bir sorun oluştu. Lütfen tekrar deneyin.',
        'Ekleme Hatası'
      );
    }
  };

  const handleAddDietProgram = async (
    programData: CreateDietProgramRequest
  ) => {
    try {
      await dietProgramApi.createDietProgram(programData).then((res) => {
        setDietPrograms((prev) => [...prev, res.dietProgram]);
        showSuccess('Diyet programı başarıyla oluşturuldu!', 'Program Eklendi');
      });
      setIsDietProgramModalOpen(false);
    } catch (err) {
      showError(
        'Diyet programı eklenirken bir sorun oluştu. Lütfen tekrar deneyin.',
        'Ekleme Hatası'
      );
    }
  };

  const handleOpenClientDetailModalWithTab = (
    client: Client,
    tab: 'info' | 'nutrition' | 'weight' | 'appointments' | 'programs'
  ) => {
    setSelectedClient(client);
    setSelectedClientTab(tab);
    setIsClientDetailModalOpen(true);
  };

  const handleCloseClientDetailModal = () => {
    setIsClientDetailModalOpen(false);
    setSelectedClient(null);
    setSelectedClientTab('info');
  };

  const handleClientUpdated = async () => {
    await refreshClients();
  };

  // Meals verisini çek
  useEffect(() => {
    const fetchMeals = async () => {
      try {
        const mealsData = await mealApi.getAllMeals();
        setMeals(mealsData);
      } catch (err) {
        console.error('Error fetching meals:', err);
      }
    };
    fetchMeals();
  }, []);

  // İstatistikler ve hesaplamalar
  const stats = useMemo(() => {
    const now = new Date();
    const thisMonth = now.getMonth();
    const thisYear = now.getFullYear();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    // Bu ay eklenen danışanlar
    const clientsThisMonth = clients.filter((client) => {
      const createdDate = new Date(client.createdAt);
      return (
        createdDate.getMonth() === thisMonth &&
        createdDate.getFullYear() === thisYear
      );
    });

    // Bu ay randevular
    const appointmentsThisMonth = appointments.filter((apt) => {
      const appointmentDate = new Date(apt.appointmentDate);
      return (
        appointmentDate.getMonth() === thisMonth &&
        appointmentDate.getFullYear() === thisYear
      );
    });

    // Yaklaşan randevular (bugünden itibaren)
    const upcomingAppointments = appointments
      .filter((apt) => {
        const appointmentDate = new Date(apt.appointmentDate);
        return appointmentDate >= today;
      })
      .sort((a, b) => {
        const dateA = new Date(a.appointmentDate + 'T' + a.startTime);
        const dateB = new Date(b.appointmentDate + 'T' + b.startTime);
        return dateA.getTime() - dateB.getTime();
      })
      .slice(0, 5);

    // Aktif programı olan danışanlar (status "Aktif" ve bitiş tarihi geçmemiş)
    const clientsWithPrograms = clients.filter((client) => {
      return dietPrograms.some((program) => {
        if (program.clientId !== client.id) return false;
        if (program.status !== 'Aktif') return false;
        const endDate = program.endDate ? new Date(program.endDate) : null;
        return !endDate || endDate >= today;
      });
    });

    // Bugünkü randevular
    const todayAppointments = appointments
      .filter((apt) => {
        const appointmentDate = new Date(apt.appointmentDate);
        return (
          appointmentDate.getDate() === today.getDate() &&
          appointmentDate.getMonth() === today.getMonth() &&
          appointmentDate.getFullYear() === today.getFullYear()
        );
      })
      .sort((a, b) => {
        const dateA = new Date(a.appointmentDate + 'T' + a.startTime);
        const dateB = new Date(b.appointmentDate + 'T' + b.startTime);
        return dateA.getTime() - dateB.getTime();
      });

    // Bu hafta randevular (bugünden itibaren 7 gün)
    const weekStart = new Date(today);
    const weekEnd = new Date(today);
    weekEnd.setDate(weekEnd.getDate() + 7);
    const thisWeekAppointments = appointments.filter((apt) => {
      const appointmentDate = new Date(apt.appointmentDate);
      return appointmentDate >= weekStart && appointmentDate < weekEnd;
    });

    // Bu ayın randevuları (sıralı)
    const appointmentsThisMonthSorted = appointmentsThisMonth.sort((a, b) => {
      const dateA = new Date(a.appointmentDate + 'T' + a.startTime);
      const dateB = new Date(b.appointmentDate + 'T' + b.startTime);
      return dateA.getTime() - dateB.getTime();
    });

    // Son eklenen danışanlar
    const recentClients = [...clients]
      .sort((a, b) => {
        const dateA = new Date(a.createdAt);
        const dateB = new Date(b.createdAt);
        return dateB.getTime() - dateA.getTime();
      })
      .slice(0, 5);

    // Aktif programlar listesi (sadece status'u "Aktif" olan ve bitiş tarihi geçmemiş)
    const activeProgramsList = dietPrograms
      .filter((program) => {
        if (program.status !== 'Aktif') return false;
        const endDate = program.endDate ? new Date(program.endDate) : null;
        return !endDate || endDate >= today;
      })
      .slice(0, 5);

    // Bugünkü meal'ler
    const todayMeals = meals.filter((meal) => {
      const mealDate = new Date(meal.mealDate);
      return (
        mealDate.getDate() === today.getDate() &&
        mealDate.getMonth() === today.getMonth() &&
        mealDate.getFullYear() === today.getFullYear()
      );
    });

    // Programı aşan danışanlar (bugünkü kalori alımı > hedef kalori)
    const clientsExceedingLimit = clients
      .map((client) => {
        const clientMeals = todayMeals.filter(
          (meal) => meal.clientId === client.id
        );
        const totalCalories = clientMeals.reduce(
          (sum, meal) => sum + meal.calories,
          0
        );
        const targetCalories = client.dailyCalories || 0;

        if (targetCalories > 0 && totalCalories > targetCalories) {
          return {
            client,
            totalCalories,
            targetCalories,
            excess: totalCalories - targetCalories,
            percentage: ((totalCalories / targetCalories) * 100).toFixed(1),
          };
        }
        return null;
      })
      .filter((item): item is NonNullable<typeof item> => item !== null)
      .sort((a, b) => b.excess - a.excess)
      .slice(0, 5);

    return {
      totalClients: clients.length,
      clientsThisMonth: clientsThisMonth.length,
      totalAppointments: appointments.length,
      appointmentsThisMonth: appointmentsThisMonth.length,
      todayAppointments: todayAppointments.length,
      todayAppointmentsList: todayAppointments,
      appointmentsThisMonthList: appointmentsThisMonthSorted,
      upcomingAppointments,
      thisWeekAppointments: thisWeekAppointments.length,
      totalPrograms: activeProgramsList.length,
      clientsWithPrograms: clientsWithPrograms.length,
      recentClients,
      activeProgramsList,
      clientsExceedingLimit,
      totalExceedingClients: clientsExceedingLimit.length,
    };
  }, [clients, appointments, dietPrograms, meals]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const formatTime = (timeString: string) => {
    return timeString.substring(0, 5);
  };

  if (loading) {
    return (
      <Loading
        size="large"
        variant="wave"
        mode="inline"
        message="Dashboard yükleniyor..."
      />
    );
  }

  return (
    <>
      {error && (
        <Paper elevation={2} sx={{ p: 2, mb: 3, backgroundColor: '#ffebee' }}>
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        </Paper>
      )}

      {/* Hoş Geldiniz Başlığı */}

      {/* Hoş Geldiniz Başlığı */}

      {/* Ana İstatistik Kartları */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Toplam Danışan */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Box
            sx={{
              perspective: '1000px',
              height: '100%',
              '&:hover .flip-card': {
                transform: 'rotateY(180deg)',
              },
              '&:hover .back-face': {
                backfaceVisibility: 'visible',
                WebkitBackfaceVisibility: 'visible',
                opacity: 1,
              },
            }}
          >
            <Card
              elevation={0}
              className="flip-card"
              sx={{
                borderRadius: 4,
                background: colors.background.paper,
                border: `1px solid ${colors.border.light}`,
                position: 'relative',
                height: '100%',
                transformStyle: 'preserve-3d',
                transformOrigin: 'center center',
                transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'pointer',
              }}
            >
              {/* Ön Yüz */}
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  backfaceVisibility: 'hidden',
                  WebkitBackfaceVisibility: 'hidden',
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={1.5}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1.5,
                        background: colors.alpha.primary10,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <PeopleIcon
                        sx={{ fontSize: 20, color: colors.primary.main }}
                      />
                    </Box>
                    <TrendingUpIcon
                      sx={{
                        opacity: 0.15,
                        fontSize: 32,
                        color: colors.primary.main,
                      }}
                    />
                  </Box>
                  <Typography
                    variant="h4"
                    fontWeight={700}
                    sx={{ mb: 0.5, color: colors.text.primary }}
                  >
                    {stats.totalClients}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: colors.text.secondary,
                      mb: 1,
                      fontSize: '0.875rem',
                    }}
                  >
                    Toplam Danışan
                  </Typography>
                  {stats.clientsThisMonth > 0 && (
                    <Chip
                      label={`+${stats.clientsThisMonth} bu ay`}
                      size="small"
                      sx={{
                        mt: 0.5,
                        background: colors.alpha.primary10,
                        color: colors.primary.main,
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        height: 24,
                        border: `1px solid ${colors.primary.light}`,
                      }}
                    />
                  )}
                </CardContent>
              </Box>

              {/* Arka Yüz */}
              <Box
                className="back-face"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backfaceVisibility: 'hidden',
                  WebkitBackfaceVisibility: 'hidden',
                  opacity: 0,
                  transform: 'rotateY(180deg)',
                  borderRadius: 4,
                  background: colors.background.paper,
                  border: `1px solid ${colors.primary.main}`,
                  overflow: 'auto',
                  scrollbarWidth: 'none', // Firefox
                  '&::-webkit-scrollbar': {
                    display: 'none', // Chrome, Safari, Edge
                  },
                  transition:
                    'opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1), backfaceVisibility 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                <CardContent sx={{ p: 2, height: '100%' }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={1.5}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      sx={{ color: colors.primary.main, fontSize: '1rem' }}
                    >
                      Son Danışanlar
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant="h5"
                        fontWeight={700}
                        sx={{ color: colors.primary.main }}
                      >
                        {stats.totalClients}
                      </Typography>
                      <PeopleIcon
                        sx={{
                          fontSize: 24,
                          color: colors.primary.main,
                          opacity: 0.7,
                        }}
                      />
                    </Box>
                  </Box>
                  {stats.recentClients.length === 0 ? (
                    <Box
                      sx={{
                        textAlign: 'center',
                        py: 3,
                        color: colors.text.secondary,
                      }}
                    >
                      <PeopleIcon
                        sx={{ fontSize: 40, mb: 1.5, opacity: 0.3 }}
                      />
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        Henüz danışan bulunmuyor
                      </Typography>
                    </Box>
                  ) : (
                    <Stack spacing={1}>
                      {stats.recentClients.map((client) => (
                        <Box
                          key={client.id}
                          onClick={() =>
                            handleOpenClientDetailModalWithTab(client, 'info')
                          }
                          sx={{
                            p: 1.25,
                            borderRadius: 1.5,
                            border: `1px solid ${colors.border.light}`,
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                              background: colors.hover.background,
                              borderColor: colors.primary.main,
                            },
                          }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{
                              color: colors.text.primary,
                              fontSize: '0.875rem',
                            }}
                          >
                            {client.firstName} {client.lastName}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: colors.text.secondary }}
                          >
                            {client.email}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </CardContent>
              </Box>
            </Card>
          </Box>
        </Grid>

        {/* Bu Ay Randevular */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Box
            sx={{
              perspective: '1000px',
              height: '100%',
              '&:hover .flip-card': {
                transform: 'rotateY(180deg)',
              },
              '&:hover .back-face': {
                backfaceVisibility: 'visible',
                WebkitBackfaceVisibility: 'visible',
                opacity: 1,
              },
            }}
          >
            <Card
              elevation={0}
              className="flip-card"
              sx={{
                borderRadius: 4,
                background: colors.background.paper,
                border: `1px solid ${colors.border.light}`,
                position: 'relative',
                height: '100%',
                transformStyle: 'preserve-3d',
                transformOrigin: 'center center',
                transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'pointer',
              }}
            >
              {/* Ön Yüz */}
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  backfaceVisibility: 'hidden',
                  WebkitBackfaceVisibility: 'hidden',
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={1.5}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1.5,
                        background: colors.alpha.primary10,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <CalendarIcon
                        sx={{ fontSize: 20, color: colors.primary.main }}
                      />
                    </Box>
                    <ScheduleIcon
                      sx={{
                        opacity: 0.15,
                        fontSize: 32,
                        color: colors.primary.main,
                      }}
                    />
                  </Box>
                  <Typography
                    variant="h4"
                    fontWeight={700}
                    sx={{ mb: 0.5, color: colors.text.primary }}
                  >
                    {stats.appointmentsThisMonth}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: colors.text.secondary,
                      mb: 1,
                      fontSize: '0.875rem',
                    }}
                  >
                    Bu Ay Randevular
                  </Typography>
                  <Chip
                    label={`${stats.totalAppointments} toplam`}
                    size="small"
                    sx={{
                      mt: 0.5,
                      background: colors.alpha.primary10,
                      color: colors.primary.main,
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      height: 24,
                      border: `1px solid ${colors.primary.light}`,
                    }}
                  />
                </CardContent>
              </Box>

              {/* Arka Yüz */}
              <Box
                className="back-face"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backfaceVisibility: 'hidden',
                  WebkitBackfaceVisibility: 'hidden',
                  opacity: 0,
                  transform: 'rotateY(180deg)',
                  borderRadius: 4,
                  background: colors.background.paper,
                  border: `1px solid ${colors.primary.main}`,
                  overflow: 'auto',
                  scrollbarWidth: 'none', // Firefox
                  '&::-webkit-scrollbar': {
                    display: 'none', // Chrome, Safari, Edge
                  },
                  transition:
                    'opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1), backfaceVisibility 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                <CardContent sx={{ p: 2, height: '100%' }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={1.5}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      sx={{ color: colors.primary.main, fontSize: '1rem' }}
                    >
                      Bu Ayın Randevuları
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant="h5"
                        fontWeight={700}
                        sx={{ color: colors.primary.main }}
                      >
                        {stats.appointmentsThisMonth}
                      </Typography>
                      <CalendarIcon
                        sx={{
                          fontSize: 24,
                          color: colors.primary.main,
                          opacity: 0.7,
                        }}
                      />
                    </Box>
                  </Box>
                  {stats.appointmentsThisMonthList.length === 0 ? (
                    <Box
                      sx={{
                        textAlign: 'center',
                        py: 3,
                        color: colors.text.secondary,
                      }}
                    >
                      <CalendarIcon
                        sx={{ fontSize: 40, mb: 1.5, opacity: 0.3 }}
                      />
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        Bu ay randevu bulunmuyor
                      </Typography>
                    </Box>
                  ) : (
                    <Stack spacing={1}>
                      {stats.appointmentsThisMonthList
                        .slice(0, 5)
                        .map((apt) => {
                          const client = clients.find(
                            (c) => c.id === apt.clientId
                          );
                          return (
                            <Box
                              key={apt.id}
                              onClick={() => {
                                if (client) {
                                  handleOpenClientDetailModalWithTab(
                                    client,
                                    'appointments'
                                  );
                                }
                              }}
                              sx={{
                                p: 1.25,
                                borderRadius: 1.5,
                                border: `1px solid ${colors.border.light}`,
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                '&:hover': {
                                  background: colors.hover.background,
                                  borderColor: colors.primary.main,
                                },
                              }}
                            >
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                sx={{
                                  fontSize: '0.875rem',
                                  color: colors.text.primary,
                                }}
                              >
                                {apt.clientName}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: colors.text.secondary }}
                              >
                                {formatDate(apt.appointmentDate)} •{' '}
                                {formatTime(apt.startTime)}
                              </Typography>
                            </Box>
                          );
                        })}
                    </Stack>
                  )}
                </CardContent>
              </Box>
            </Card>
          </Box>
        </Grid>

        {/* Aktif Diyet Programları */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Box
            sx={{
              perspective: '1000px',
              height: '100%',
              '&:hover .flip-card': {
                transform: 'rotateY(180deg)',
              },
              '&:hover .back-face': {
                backfaceVisibility: 'visible',
                WebkitBackfaceVisibility: 'visible',
                opacity: 1,
              },
            }}
          >
            <Card
              elevation={0}
              className="flip-card"
              sx={{
                borderRadius: 4,
                background: colors.background.paper,
                border: `1px solid ${colors.border.light}`,
                position: 'relative',
                height: '100%',
                transformStyle: 'preserve-3d',
                transformOrigin: 'center center',
                transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'pointer',
              }}
            >
              {/* Ön Yüz */}
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  backfaceVisibility: 'hidden',
                  WebkitBackfaceVisibility: 'hidden',
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={1.5}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1.5,
                        background: colors.alpha.primary10,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <AssignmentIcon
                        sx={{ fontSize: 20, color: colors.primary.main }}
                      />
                    </Box>
                    <MenuBookIcon
                      sx={{
                        opacity: 0.15,
                        fontSize: 32,
                        color: colors.primary.main,
                      }}
                    />
                  </Box>
                  <Typography
                    variant="h4"
                    fontWeight={700}
                    sx={{ mb: 0.5, color: colors.text.primary }}
                  >
                    {stats.activeProgramsList.length}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: colors.text.secondary,
                      mb: 1,
                      fontSize: '0.875rem',
                    }}
                  >
                    Aktif Diyet Programları
                  </Typography>
                  <Chip
                    label={`${stats.clientsWithPrograms} danışan`}
                    size="small"
                    sx={{
                      mt: 0.5,
                      background: colors.alpha.primary10,
                      color: colors.primary.main,
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      height: 24,
                      border: `1px solid ${colors.primary.light}`,
                    }}
                  />
                </CardContent>
              </Box>

              {/* Arka Yüz */}
              <Box
                className="back-face"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backfaceVisibility: 'hidden',
                  WebkitBackfaceVisibility: 'hidden',
                  opacity: 0,
                  transform: 'rotateY(180deg)',
                  borderRadius: 4,
                  background: colors.background.paper,
                  border: `1px solid ${colors.primary.main}`,
                  overflow: 'auto',
                  scrollbarWidth: 'none', // Firefox
                  '&::-webkit-scrollbar': {
                    display: 'none', // Chrome, Safari, Edge
                  },
                  transition:
                    'opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1), backfaceVisibility 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                <CardContent sx={{ p: 2, height: '100%' }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={1.5}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      sx={{ color: colors.primary.main, fontSize: '1rem' }}
                    >
                      Aktif Diyet Programları
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant="h5"
                        fontWeight={700}
                        sx={{ color: colors.primary.main }}
                      >
                        {stats.totalPrograms}
                      </Typography>
                      <AssignmentIcon
                        sx={{
                          fontSize: 24,
                          color: colors.primary.main,
                          opacity: 0.7,
                        }}
                      />
                    </Box>
                  </Box>
                  {stats.activeProgramsList.length === 0 ? (
                    <Box
                      sx={{
                        textAlign: 'center',
                        py: 3,
                        color: colors.text.secondary,
                      }}
                    >
                      <AssignmentIcon
                        sx={{ fontSize: 40, mb: 1.5, opacity: 0.3 }}
                      />
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        Aktif diyet programı bulunmuyor
                      </Typography>
                    </Box>
                  ) : (
                    <Stack spacing={1}>
                      {stats.activeProgramsList.map((program) => {
                        const client = clients.find(
                          (c) => c.id === program.clientId
                        );
                        return (
                          <Box
                            key={program.id}
                            onClick={() => {
                              if (client) {
                                handleOpenClientDetailModalWithTab(
                                  client,
                                  'programs'
                                );
                              }
                            }}
                            sx={{
                              p: 1.25,
                              borderRadius: 1.5,
                              border: `1px solid ${colors.border.light}`,
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              '&:hover': {
                                background: colors.hover.background,
                                borderColor: colors.primary.main,
                              },
                            }}
                          >
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              sx={{
                                fontSize: '0.875rem',
                                color: colors.text.primary,
                              }}
                            >
                              {program.programName || 'Program'}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: colors.text.secondary }}
                            >
                              {client
                                ? `${client.firstName} ${client.lastName}`
                                : 'Danışan bulunamadı'}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Stack>
                  )}
                </CardContent>
              </Box>
            </Card>
          </Box>
        </Grid>

        {/* Bugünkü Randevular */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Box
            sx={{
              perspective: '1000px',
              height: '100%',
              '&:hover .flip-card': {
                transform: 'rotateY(180deg)',
              },
              '&:hover .back-face': {
                backfaceVisibility: 'visible',
                WebkitBackfaceVisibility: 'visible',
                opacity: 1,
              },
            }}
          >
            <Card
              elevation={0}
              className="flip-card"
              sx={{
                borderRadius: 4,
                background: colors.background.paper,
                border: `1px solid ${colors.border.light}`,
                position: 'relative',
                height: '100%',
                transformStyle: 'preserve-3d',
                transformOrigin: 'center center',
                transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'pointer',
              }}
            >
              {/* Ön Yüz */}
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  backfaceVisibility: 'hidden',
                  WebkitBackfaceVisibility: 'hidden',
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={1.5}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1.5,
                        background: colors.status.warning.background,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <AccessTimeIcon
                        sx={{ fontSize: 20, color: colors.status.warning.main }}
                      />
                    </Box>
                    <EventIcon
                      sx={{
                        opacity: 0.15,
                        fontSize: 32,
                        color: colors.status.warning.main,
                      }}
                    />
                  </Box>
                  <Typography
                    variant="h4"
                    fontWeight={700}
                    sx={{ mb: 0.5, color: colors.text.primary }}
                  >
                    {stats.todayAppointments}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: colors.text.secondary,
                      mb: 1,
                      fontSize: '0.875rem',
                    }}
                  >
                    Bugünkü Randevular
                  </Typography>
                  <Chip
                    label="Bugün"
                    size="small"
                    sx={{
                      mt: 0.5,
                      background: colors.status.warning.background,
                      color: colors.status.warning.main,
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      height: 24,
                      border: `1px solid ${colors.status.warning.border}`,
                    }}
                  />
                </CardContent>
              </Box>

              {/* Arka Yüz */}
              <Box
                className="back-face"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  backfaceVisibility: 'hidden',
                  WebkitBackfaceVisibility: 'hidden',
                  opacity: 0,
                  transform: 'rotateY(180deg)',
                  borderRadius: 4,
                  background: colors.background.paper,
                  border: `1px solid ${colors.status.warning.main}`,
                  overflow: 'auto',
                  scrollbarWidth: 'none', // Firefox
                  '&::-webkit-scrollbar': {
                    display: 'none', // Chrome, Safari, Edge
                  },
                  transition:
                    'opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1), backfaceVisibility 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                <CardContent sx={{ p: 2, height: '100%' }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={1.5}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      sx={{
                        color: colors.status.warning.main,
                        fontSize: '1rem',
                      }}
                    >
                      Bugünkü Randevular
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant="h4"
                        fontWeight={700}
                        sx={{ color: colors.status.warning.main }}
                      >
                        {stats.todayAppointments}
                      </Typography>
                      <AccessTimeIcon
                        sx={{
                          fontSize: 24,
                          color: colors.status.warning.main,
                          opacity: 0.7,
                        }}
                      />
                    </Box>
                  </Box>
                  {stats.todayAppointmentsList.length === 0 ? (
                    <Box
                      sx={{
                        textAlign: 'center',
                        py: 3,
                        color: colors.text.secondary,
                      }}
                    >
                      <EventIcon sx={{ fontSize: 40, mb: 1.5, opacity: 0.3 }} />
                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                        Bugün randevu bulunmuyor
                      </Typography>
                    </Box>
                  ) : (
                    <Stack spacing={1}>
                      {stats.todayAppointmentsList.map((apt) => {
                        const client = clients.find(
                          (c) => c.id === apt.clientId
                        );
                        return (
                          <Box
                            key={apt.id}
                            onClick={() => {
                              if (client) {
                                handleOpenClientDetailModalWithTab(
                                  client,
                                  'appointments'
                                );
                              }
                            }}
                            sx={{
                              p: 1.25,
                              borderRadius: 1.5,
                              border: `1px solid ${colors.border.light}`,
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              '&:hover': {
                                background: colors.hover.background,
                                borderColor: colors.status.warning.main,
                              },
                            }}
                          >
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              sx={{
                                fontSize: '0.875rem',
                                color: colors.text.primary,
                              }}
                            >
                              {apt.clientName}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: colors.text.secondary }}
                            >
                              {formatTime(apt.startTime)} -{' '}
                              {formatTime(apt.endTime)}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Stack>
                  )}
                </CardContent>
              </Box>
            </Card>
          </Box>
        </Grid>
      </Grid>

      {/* Hızlı Erişim Butonları */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          borderRadius: 3,
          background: colors.background.paper,
          border: `1px solid ${colors.border.light}`,
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight={600}
          sx={{ mb: 2, color: colors.text.primary, fontSize: '1.1rem' }}
        >
          Hızlı İşlemler
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => setIsAddClientModalOpen(true)}
              sx={{
                py: 1.5,
                borderRadius: 2.5,
                background: colors.primary.main,
                textTransform: 'none',
                fontSize: '0.95rem',
                fontWeight: 600,
                boxShadow: 'none',
                '&:hover': {
                  background: colors.primary.dark,
                  transform: 'translateY(-2px)',
                  boxShadow: colors.shadows.button,
                },
                transition: 'all 0.3s ease',
              }}
            >
              Yeni Danışan
            </Button>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<EventIcon />}
              onClick={() => {
                if (clients.length === 0) {
                  showWarning(
                    'Randevu oluşturmak için önce en az bir danışan eklemeniz gerekiyor.',
                    'Danışan Bulunamadı'
                  );
                  return;
                }
                setIsAppointmentModalOpen(true);
              }}
              sx={{
                py: 1.5,
                borderRadius: 2.5,
                borderColor: colors.primary.main,
                color: colors.primary.main,
                textTransform: 'none',
                fontSize: '0.95rem',
                fontWeight: 600,
                '&:hover': {
                  borderColor: colors.primary.dark,
                  background: colors.alpha.primary05,
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Randevu Oluştur
            </Button>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<DescriptionIcon />}
              onClick={() => {
                if (clients.length === 0) {
                  showWarning(
                    'Diyet programı oluşturmak için önce en az bir danışan eklemeniz gerekiyor.',
                    'Danışan Bulunamadı'
                  );
                  return;
                }
                setIsDietProgramModalOpen(true);
              }}
              sx={{
                py: 1.5,
                borderRadius: 2.5,
                borderColor: colors.primary.main,
                color: colors.primary.main,
                textTransform: 'none',
                fontSize: '0.95rem',
                fontWeight: 600,
                '&:hover': {
                  borderColor: colors.primary.dark,
                  background: colors.alpha.primary05,
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Diyet Programı Hazırla
            </Button>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<GroupIcon />}
              onClick={() => navigate('/dietitian/clients')}
              sx={{
                py: 1.5,
                borderRadius: 2.5,
                borderColor: colors.primary.main,
                color: colors.primary.main,
                textTransform: 'none',
                fontSize: '0.95rem',
                fontWeight: 600,
                '&:hover': {
                  borderColor: colors.primary.dark,
                  background: colors.alpha.primary05,
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Tüm Danışanlar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={2}>
        {/* Diyet Programını Aşan Danışanlar */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 3,
              background: colors.background.paper,
              border: `1px solid ${colors.border.light}`,
              height: '100%',
              position: 'relative',
              overflow: 'hidden',
              '&:hover': {
                borderColor: colors.status.warning.main,
                boxShadow: colors.shadows.cardHover,
              },
              transition: 'all 0.3s ease',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: -20,
                right: -20,
                width: 120,
                height: 120,
                borderRadius: '50%',
                background: colors.status.warning.background,
                opacity: 0.5,
              }}
            />
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={3}
              position="relative"
            >
              <Box display="flex" alignItems="center" flex={1}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1.5,
                    background: `linear-gradient(135deg, ${colors.status.warning.main} 0%, ${colors.status.warning.dark} 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1.5,
                    boxShadow: colors.shadows.icon,
                  }}
                >
                  <MenuBookIcon sx={{ color: 'white', fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    sx={{ color: colors.text.primary, fontSize: '1rem' }}
                  >
                    Diyet Programını Aşan Danışanlar
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: colors.text.secondary, fontSize: '0.875rem' }}
                  >
                    Bugün kalori limitini aşanlar
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box position="relative">
              <Typography
                variant="h3"
                fontWeight={700}
                sx={{
                  color: colors.status.warning.main,
                  mb: 1,
                  fontSize: { xs: '2rem', sm: '2.5rem' },
                }}
              >
                {stats.totalExceedingClients}
              </Typography>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <Typography
                  variant="body2"
                  sx={{ color: colors.text.secondary }}
                >
                  {stats.totalExceedingClients > 0
                    ? `${stats.totalExceedingClients} danışan limiti aştı`
                    : 'Tüm danışanlar hedefe uygun'}
                </Typography>
              </Box>
              {stats.clientsExceedingLimit.length === 0 ? (
                <Box
                  sx={{
                    textAlign: 'center',
                    py: 3,
                    color: colors.text.secondary,
                  }}
                >
                  <MenuBookIcon sx={{ fontSize: 40, mb: 1.5, opacity: 0.3 }} />
                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                    Bugün diyet programını aşan danışan bulunmuyor
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={1}>
                  {stats.clientsExceedingLimit.map((item) => (
                    <Box
                      key={item.client.id}
                      onClick={() =>
                        handleOpenClientDetailModalWithTab(
                          item.client,
                          'nutrition'
                        )
                      }
                      sx={{
                        p: 1.5,
                        borderRadius: 1.5,
                        border: `1px solid ${colors.status.warning.border}`,
                        background: colors.status.warning.background,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          background: colors.status.warning.hover,
                          borderColor: colors.status.warning.main,
                          transform: 'translateX(4px)',
                        },
                      }}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        <Box flex={1}>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{
                              fontSize: '0.875rem',
                              color: colors.text.primary,
                              mb: 0.5,
                            }}
                          >
                            {item.client.firstName} {item.client.lastName}
                          </Typography>
                          <Box display="flex" alignItems="center" gap={2}>
                            <Typography
                              variant="body2"
                              sx={{ color: colors.text.secondary }}
                            >
                              <strong
                                style={{ color: colors.status.warning.main }}
                              >
                                {item.totalCalories.toFixed(0)}
                              </strong>{' '}
                              / {item.targetCalories.toFixed(0)} kcal
                            </Typography>
                            <Chip
                              label={`+${item.excess.toFixed(0)} kcal`}
                              size="small"
                              sx={{
                                background: colors.status.warning.main,
                                color: 'white',
                                fontWeight: 600,
                                fontSize: '0.7rem',
                              }}
                            />
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: colors.status.warning.dark,
                              mt: 0.5,
                              display: 'block',
                            }}
                          >
                            %{item.percentage} fazla
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Danışan İstatistikleri */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 3,
              background: colors.background.paper,
              border: `1px solid ${colors.border.light}`,
              height: '100%',
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={2}
            >
              <Box display="flex" alignItems="center">
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    background: colors.gradients.primaryLight,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2,
                  }}
                >
                  <AssessmentIcon
                    sx={{ color: colors.primary.main, fontSize: 24 }}
                  />
                </Box>
                <Box>
                  <Typography
                    variant="h6"
                    fontWeight={600}
                    sx={{ color: colors.text.primary }}
                  >
                    Danışan Özeti
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: colors.text.secondary }}
                  >
                    Genel bilgiler ve istatistikler
                  </Typography>
                </Box>
              </Box>
              <IconButton
                size="small"
                onClick={() => navigate('/dietitian/clients')}
                sx={{
                  width: 40,
                  color: colors.primary.main,
                  '&:hover': { background: colors.alpha.primary10 },
                }}
              >
                <ArrowForwardIcon />
              </IconButton>
            </Box>
            <Stack spacing={1.5}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 1.5,
                  background: colors.alpha.primary05,
                  border: `1px solid ${colors.primary.light}`,
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  mb={0.75}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: colors.text.secondary, fontSize: '0.875rem' }}
                  >
                    Toplam Danışan
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{ color: colors.primary.main, fontSize: '1.25rem' }}
                  >
                    {stats.totalClients}
                  </Typography>
                </Box>
                {stats.clientsThisMonth > 0 && (
                  <Chip
                    icon={<TrendingUpIcon sx={{ fontSize: 14 }} />}
                    label={`Bu ay ${stats.clientsThisMonth} yeni danışan eklendi`}
                    size="small"
                    sx={{
                      mt: 0.5,
                      background: colors.chip.primaryBg,
                      color: colors.primary.main,
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      height: 24,
                    }}
                  />
                )}
              </Box>

              <Box
                sx={{
                  p: 2,
                  borderRadius: 1.5,
                  background: colors.alpha.primary05,
                  border: `1px solid ${colors.primary.light}`,
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  mb={1}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: colors.text.secondary }}
                  >
                    Aktif Diyet Programı Olan
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{ color: colors.primary.main }}
                  >
                    {stats.clientsWithPrograms}
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  sx={{ color: colors.text.secondary }}
                >
                  {stats.totalClients > 0
                    ? `${Math.round(
                      (stats.clientsWithPrograms / stats.totalClients) * 100
                    )}% danışan aktif diyet programı takip ediyor`
                    : 'Henüz danışan yok'}
                </Typography>
              </Box>

              <Box
                sx={{
                  p: 2,
                  borderRadius: 1.5,
                  background: colors.alpha.primary05,
                  border: `1px solid ${colors.primary.light}`,
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  mb={0.75}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: colors.text.secondary, fontSize: '0.875rem' }}
                  >
                    Toplam Randevu
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{ color: colors.primary.main, fontSize: '1.25rem' }}
                  >
                    {stats.totalAppointments}
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  sx={{ color: colors.text.secondary, fontSize: '0.75rem' }}
                >
                  Bu ay {stats.appointmentsThisMonth} randevu planlandı
                </Typography>
              </Box>

              <Box
                sx={{
                  p: 2,
                  borderRadius: 1.5,
                  background: colors.alpha.primary05,
                  border: `1px solid ${colors.primary.light}`,
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  mb={0.75}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: colors.text.secondary, fontSize: '0.875rem' }}
                  >
                    Aktif Diyet Programları
                  </Typography>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    sx={{ color: colors.primary.main, fontSize: '1.25rem' }}
                  >
                    {stats.totalPrograms}
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  sx={{ color: colors.text.secondary }}
                >
                  {stats.clientsWithPrograms} danışan aktif diyet programı takip ediyor
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {/* Modals */}
      <AddClientModal
        open={isAddClientModalOpen}
        onClose={() => setIsAddClientModalOpen(false)}
        onSave={handleAddClient}
      />

      <AppointmentModal
        open={isAppointmentModalOpen}
        onClose={() => setIsAppointmentModalOpen(false)}
        onSave={handleAddAppointment}
        clients={clients as any}
        dietitianId={dietitianId}
      />

      <DietProgramModal
        open={isDietProgramModalOpen}
        onClose={() => setIsDietProgramModalOpen(false)}
        onSave={handleAddDietProgram}
        clients={clients as any}
        dietitianId={dietitianId}
      />

      <ClientDetailModal
        open={isClientDetailModalOpen}
        onClose={handleCloseClientDetailModal}
        client={selectedClient}
        onClientUpdated={handleClientUpdated}
        initialTab={selectedClientTab}
      />


    </>
  );
};

export default DietitianDashboard;
